/**
 * bootstrapPlan — the ONE way a droom entry route pins the frozen check plan.
 *
 * 18-Aug RCA (owner item #2, HDFC "DigiLocker has not happened"): the invite-link entry route
 * `/s/:sessionToken` never read `GET /sessions/{id}/checks`, so the store kept `plan=[]`, the
 * hardcoded legacy FLOW_ORDER ran, and the Aadhaar step defaulted its requirement to OPTIONAL — a
 * REQUIRED DigiLocker rendered a skip, the customer advanced, and the be's queue gate refused them
 * four times in a loop. Only /start and /call read the plan (16/17-Aug fixes). This helper is
 * shared by EVERY entry so no route can drift again.
 *
 * Contract:
 *   • called AFTER the token exchange resolved non-null (the caller chains it; `request()` also
 *     awaits any in-flight exchange as a belt);
 *   • success ⇒ `initCheckPlan` (planState 'ready' for a non-empty plan) + served branding pin;
 *   • empty plan (real backend) ⇒ reported + planState 'unknown' (the shell blocks with a retry);
 *   • failure ⇒ reported + `markPlanUnknown` (a persisted non-empty plan for the same reload is
 *     kept — the plan is write-once server-side).
 *
 * Returns the DTO on success (the /call route feeds it to `decideEntry`) or null.
 */
import { fetchStartBootstrap } from '@/api/session';
import { useSessionStore } from '@/store/sessionStore';
import { parseServedBranding } from '@/theme/servedTenant';
import type { SessionChecksDTO } from '@/types';
import { classifyPlanReadFailure, reportPlanReadFailure } from './planTelemetry';

export async function bootstrapPlan(
  sessionId: string,
  isActive: () => boolean = () => true,
): Promise<SessionChecksDTO | null> {
  const store = useSessionStore.getState();
  store.beginPlanRead();
  try {
    const dto = await fetchStartBootstrap(sessionId);
    if (!isActive()) return dto;
    const st = useSessionStore.getState();
    st.initCheckPlan({ plan: dto.plan, flowKey: dto.flowKey ?? null });
    const served = parseServedBranding(dto.branding);
    if (served) st.setServedBranding(served);
    if (dto.plan.length === 0) reportPlanReadFailure(sessionId, 'empty');
    return dto;
  } catch (e: unknown) {
    if (!isActive()) return null;
    reportPlanReadFailure(sessionId, classifyPlanReadFailure(e));
    useSessionStore.getState().markPlanUnknown();
    return null;
  }
}
